/**
 * BuyerBriefPage.tsx — 2004va
 *
 * Buyers set and manage their standing briefs.
 * Each brief is a set of criteria the AI matches against every upload.
 * Active briefs run continuously — matching shots land in the buyer's inbox.
 *
 * Design: clean form, no unnecessary complexity.
 * The brief is the buyer's instruction to the AI — plain language matters.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, ToggleLeft, ToggleRight, Edit2, X, Check } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import { Badge, Button, Spinner } from '@/components/ui'
import { cn } from '@/components/ui/utils'

// ── Types ─────────────────────────────────────────────────────────────────────

interface BuyerBrief {
  id:                 string
  participant_id:     string
  title:              string
  description:        string | null
  keywords:           string[]
  excluded_keywords:  string[]
  preferred_moods:    string[]
  min_grade:          'A' | 'B' | 'C' | 'D'
  min_score:          number
  media_types:        string[]
  usage_intent:       string
  max_budget_gbp:     number | null
  active:             boolean
  created_at:         string
  updated_at:         string
}

interface BriefForm {
  title:              string
  description:        string
  keywords:           string       // comma-separated input
  excluded_keywords:  string
  preferred_moods:    string[]
  min_grade:          'A' | 'B' | 'C' | 'D'
  media_types:        string[]
  usage_intent:       string
  max_budget_gbp:     string
}

const EMPTY_FORM: BriefForm = {
  title:             '',
  description:       '',
  keywords:          '',
  excluded_keywords: '',
  preferred_moods:   [],
  min_grade:         'B',
  media_types:       ['photo'],
  usage_intent:      'editorial',
  max_budget_gbp:    '',
}

const MOODS = ['vibrant','intimate','dramatic','energetic','raw','tense','joyful','chaotic','serene','powerful']
const GRADE_LABEL = { A: 'A only — exceptional', B: 'B+ — strong', C: 'C+ — decent', D: 'All grades' }
const USAGE_TYPES = ['editorial','commercial','social','exclusive']

// ── Supabase helpers ──────────────────────────────────────────────────────────

async function fetchBriefs(participantId: string): Promise<BuyerBrief[]> {
  const { data, error } = await supabase
    .from('buyer_briefs')
    .select('*')
    .eq('participant_id', participantId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data ?? []
}

async function saveBrief(participantId: string, form: BriefForm, id?: string): Promise<BuyerBrief> {
  const payload = {
    participant_id:    participantId,
    title:             form.title.trim(),
    description:       form.description.trim() || null,
    keywords:          form.keywords.split(',').map(k => k.trim()).filter(Boolean),
    excluded_keywords: form.excluded_keywords.split(',').map(k => k.trim()).filter(Boolean),
    preferred_moods:   form.preferred_moods,
    min_grade:         form.min_grade,
    media_types:       form.media_types,
    usage_intent:      form.usage_intent,
    max_budget_gbp:    form.max_budget_gbp ? parseFloat(form.max_budget_gbp) : null,
    updated_at:        new Date().toISOString(),
    active:            true,
  }

  if (id) {
    const { data, error } = await supabase.from('buyer_briefs').update(payload).eq('id', id).select().single()
    if (error) throw error
    return data
  } else {
    const { data, error } = await supabase.from('buyer_briefs').insert(payload).select().single()
    if (error) throw error
    return data
  }
}

async function toggleBrief(id: string, active: boolean): Promise<void> {
  const { error } = await supabase.from('buyer_briefs').update({ active }).eq('id', id)
  if (error) throw error
}

async function deleteBrief(id: string): Promise<void> {
  const { error } = await supabase.from('buyer_briefs').delete().eq('id', id)
  if (error) throw error
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function BuyerBriefPage() {
  const { participant } = useAuthStore()
  const qc = useQueryClient()

  const [showForm, setShowForm]   = useState(false)
  const [editId,   setEditId]     = useState<string | null>(null)
  const [form,     setForm]       = useState<BriefForm>(EMPTY_FORM)

  const { data: briefs = [], isLoading } = useQuery<BuyerBrief[]>({
    queryKey: ['buyer-briefs', participant?.id],
    queryFn:  () => fetchBriefs(participant!.id),
    enabled:  !!participant,
  })

  const saveMutation = useMutation({
    mutationFn: () => saveBrief(participant!.id, form, editId ?? undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['buyer-briefs'] })
      setShowForm(false)
      setEditId(null)
      setForm(EMPTY_FORM)
    },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => toggleBrief(id, active),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['buyer-briefs'] }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteBrief(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['buyer-briefs'] }),
  })

  function handleEdit(brief: BuyerBrief) {
    setForm({
      title:             brief.title,
      description:       brief.description ?? '',
      keywords:          brief.keywords.join(', '),
      excluded_keywords: brief.excluded_keywords.join(', '),
      preferred_moods:   brief.preferred_moods,
      min_grade:         brief.min_grade,
      media_types:       brief.media_types,
      usage_intent:      brief.usage_intent,
      max_budget_gbp:    brief.max_budget_gbp?.toString() ?? '',
    })
    setEditId(brief.id)
    setShowForm(true)
  }

  function toggleMood(mood: string) {
    setForm(f => ({
      ...f,
      preferred_moods: f.preferred_moods.includes(mood)
        ? f.preferred_moods.filter(m => m !== mood)
        : [...f.preferred_moods, mood],
    }))
  }

  function toggleMediaType(type: string) {
    setForm(f => ({
      ...f,
      media_types: f.media_types.includes(type)
        ? f.media_types.filter(t => t !== type)
        : [...f.media_types, type],
    }))
  }

  if (isLoading) return (
    <div className="flex justify-center py-12"><Spinner size={24} /></div>
  )

  return (
    <div className="space-y-6 animate-fade-in">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-bold text-lg">Buyer Briefs</h2>
          <p className="text-room-muted text-xs mt-0.5">
            Active briefs run continuously — matching shots land in your inbox automatically.
          </p>
        </div>
        {!showForm && (
          <Button
            variant="accent"
            size="sm"
            icon={<Plus size={14} />}
            onClick={() => { setForm(EMPTY_FORM); setEditId(null); setShowForm(true) }}
          >
            New brief
          </Button>
        )}
      </div>

      {/* ── Brief form ── */}
      {showForm && (
        <div className="bg-room-surface border border-room-border rounded-2xl p-5 space-y-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">
              {editId ? 'Edit brief' : 'New brief'}
            </h3>
            <button type="button" onClick={() => { setShowForm(false); setEditId(null); setForm(EMPTY_FORM) }}
              className="text-room-muted hover:text-white transition-colors">
              <X size={14} />
            </button>
          </div>

          {/* Title */}
          <div>
            <label className="text-xs text-room-muted font-semibold uppercase tracking-widest block mb-1.5">
              Brief name
            </label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Ibiza club nights — press editorial"
              className="w-full bg-black/30 border border-room-border rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-room-muted focus:outline-none focus:border-room-accent/50 transition-colors"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs text-room-muted font-semibold uppercase tracking-widest block mb-1.5">
              What are you looking for? <span className="text-room-muted/50 normal-case font-normal">(optional)</span>
            </label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="e.g. Crowd energy shots, headline acts on stage, VIP areas. No posed photos."
              rows={2}
              className="w-full bg-black/30 border border-room-border rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-room-muted resize-none focus:outline-none focus:border-room-accent/50 transition-colors leading-relaxed"
            />
          </div>

          {/* Keywords */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-room-muted font-semibold uppercase tracking-widest block mb-1.5">
                Keywords
              </label>
              <input
                type="text"
                value={form.keywords}
                onChange={e => setForm(f => ({ ...f, keywords: e.target.value }))}
                placeholder="dj, crowd, ibiza, stage"
                className="w-full bg-black/30 border border-room-border rounded-xl px-3 py-2 text-xs text-white placeholder:text-room-muted focus:outline-none focus:border-room-accent/50 transition-colors"
              />
              <p className="text-[10px] text-room-muted mt-1">Comma separated</p>
            </div>
            <div>
              <label className="text-xs text-room-muted font-semibold uppercase tracking-widest block mb-1.5">
                Exclude
              </label>
              <input
                type="text"
                value={form.excluded_keywords}
                onChange={e => setForm(f => ({ ...f, excluded_keywords: e.target.value }))}
                placeholder="posed, studio, portrait"
                className="w-full bg-black/30 border border-room-border rounded-xl px-3 py-2 text-xs text-white placeholder:text-room-muted focus:outline-none focus:border-room-accent/50 transition-colors"
              />
              <p className="text-[10px] text-room-muted mt-1">Comma separated</p>
            </div>
          </div>

          {/* Moods */}
          <div>
            <label className="text-xs text-room-muted font-semibold uppercase tracking-widest block mb-2">
              Preferred moods <span className="text-room-muted/50 normal-case font-normal">(optional)</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {MOODS.map(mood => (
                <button
                  key={mood}
                  type="button"
                  onClick={() => toggleMood(mood)}
                  className={cn(
                    'px-2.5 py-1 rounded-full text-xs font-medium transition-all',
                    form.preferred_moods.includes(mood)
                      ? 'bg-room-accent text-white'
                      : 'bg-room-border text-room-muted hover:text-white'
                  )}
                >
                  {mood}
                </button>
              ))}
            </div>
          </div>

          {/* Grade + media type + usage */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-room-muted font-semibold uppercase tracking-widest block mb-1.5">
                Min grade
              </label>
              <select
                value={form.min_grade}
                onChange={e => setForm(f => ({ ...f, min_grade: e.target.value as 'A'|'B'|'C'|'D' }))}
                className="w-full bg-black/30 border border-room-border rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-room-accent/50"
              >
                {(Object.entries(GRADE_LABEL) as [string, string][]).map(([g, l]) => (
                  <option key={g} value={g}>{l}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs text-room-muted font-semibold uppercase tracking-widest block mb-1.5">
                Media
              </label>
              <div className="flex gap-1.5 flex-wrap">
                {['photo','video','audio'].map(t => (
                  <button key={t} type="button" onClick={() => toggleMediaType(t)}
                    className={cn(
                      'px-2 py-1 rounded-lg text-xs font-medium transition-all',
                      form.media_types.includes(t) ? 'bg-room-accent text-white' : 'bg-room-border text-room-muted hover:text-white'
                    )}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-room-muted font-semibold uppercase tracking-widest block mb-1.5">
                Usage
              </label>
              <select
                value={form.usage_intent}
                onChange={e => setForm(f => ({ ...f, usage_intent: e.target.value }))}
                className="w-full bg-black/30 border border-room-border rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-room-accent/50"
              >
                {USAGE_TYPES.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>

          {/* Max budget */}
          <div>
            <label className="text-xs text-room-muted font-semibold uppercase tracking-widest block mb-1.5">
              Max budget per license (£) <span className="text-room-muted/50 normal-case font-normal">(optional)</span>
            </label>
            <input
              type="number"
              value={form.max_budget_gbp}
              onChange={e => setForm(f => ({ ...f, max_budget_gbp: e.target.value }))}
              placeholder="e.g. 250"
              className="w-full bg-black/30 border border-room-border rounded-xl px-3 py-2 text-xs text-white placeholder:text-room-muted focus:outline-none focus:border-room-accent/50 transition-colors"
            />
          </div>

          {/* Save */}
          <div className="flex gap-2 pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setShowForm(false); setEditId(null); setForm(EMPTY_FORM) }}
            >
              Cancel
            </Button>
            <Button
              variant="accent"
              size="sm"
              icon={saveMutation.isPending ? <Spinner size={13} /> : <Check size={13} />}
              onClick={() => saveMutation.mutate()}
              disabled={!form.title.trim() || saveMutation.isPending}
              className="flex-1"
            >
              {editId ? 'Save changes' : 'Create brief'}
            </Button>
          </div>
          {saveMutation.isError && (
            <p className="text-xs text-room-error">Failed to save — try again</p>
          )}
        </div>
      )}

      {/* ── Brief list ── */}
      {briefs.length === 0 && !showForm && (
        <div className="text-center py-12 space-y-2">
          <p className="text-room-muted text-sm">No briefs yet</p>
          <p className="text-room-muted/60 text-xs">Create a brief and the AI will match shots to it automatically</p>
        </div>
      )}

      <div className="space-y-3">
        {briefs.map(brief => (
          <div key={brief.id}
            className={cn(
              'bg-room-surface border rounded-xl p-4 space-y-3 transition-all',
              brief.active ? 'border-room-border' : 'border-room-border/40 opacity-60'
            )}
          >
            {/* Brief header */}
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-white text-sm font-semibold">{brief.title}</p>
                  <Badge variant={brief.active ? 'success' : 'default'}>
                    {brief.active ? 'Active' : 'Paused'}
                  </Badge>
                  <Badge variant="accent">{brief.min_grade}+</Badge>
                  <Badge variant="default">{brief.usage_intent}</Badge>
                </div>
                {brief.description && (
                  <p className="text-room-muted text-xs mt-1 leading-relaxed">{brief.description}</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-1 flex-shrink-0">
                <button type="button" onClick={() => handleEdit(brief)}
                  className="p-1.5 rounded-lg text-room-muted hover:text-white hover:bg-room-border transition-colors">
                  <Edit2 size={13} />
                </button>
                <button type="button"
                  onClick={() => toggleMutation.mutate({ id: brief.id, active: !brief.active })}
                  className="p-1.5 rounded-lg text-room-muted hover:text-white hover:bg-room-border transition-colors">
                  {brief.active ? <ToggleRight size={13} className="text-room-success" /> : <ToggleLeft size={13} />}
                </button>
                <button type="button" onClick={() => deleteMutation.mutate(brief.id)}
                  className="p-1.5 rounded-lg text-room-muted hover:text-room-error transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>

            {/* Keywords */}
            {brief.keywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {brief.keywords.map(k => (
                  <span key={k} className="px-2 py-0.5 rounded-full bg-room-accent/10 border border-room-accent/20 text-room-accent text-[10px] font-medium">
                    {k}
                  </span>
                ))}
                {brief.excluded_keywords.map(k => (
                  <span key={k} className="px-2 py-0.5 rounded-full bg-red-900/20 border border-red-800/20 text-red-400 text-[10px] font-medium">
                    −{k}
                  </span>
                ))}
              </div>
            )}

            {/* Moods + budget */}
            <div className="flex items-center gap-3 text-[10px] text-room-muted">
              {brief.preferred_moods.length > 0 && (
                <span>Moods: {brief.preferred_moods.join(', ')}</span>
              )}
              {brief.max_budget_gbp && (
                <span>Max £{brief.max_budget_gbp}/license</span>
              )}
              <span className="ml-auto">
                {brief.media_types.join(' · ')}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
